/**
 * NodeDetailPanel - Bottom panel showing selected node details
 *
 * Opens when a node is clicked in the graph.
 * Resizable via drag handle at top of panel.
 * Clicking a finding opens the right sidebar with legal text.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { X, ExternalLink, GripHorizontal } from 'lucide-react';
import { NodeType, getNodeTypeColor, getNodeTypeLabel } from './types';

export interface NodeDetailData {
  nodeId: string;
  nodeType: NodeType;
  label: string;
  content?: string;
  detail?: string;
  relevanceNote?: string;  // Why this finding matters - the reasoning/logic
  findingIds?: string[];
  sourceDocument?: string;
  sourceReference?: string;
  // Plan graph (#89)
  workspaceType?: string;  // "research" | "production" | "institution_context"
  workspaceId?: string;    // Workspace ID for plan nodes
  deliverable?: string;    // What this workspace produces
  inputs?: Array<{ deliverable: string; fromLabel: string }>;  // Upstream deliverables
  // Validation (#89)
  validationStatus?: 'approved' | 'rejected' | 'review_needed' | null;
  reviews?: Array<{
    reviewer_id: string;
    channel: string;
    verdict: string;
    rationale: string;
    severity: string;
  }>;
}

// Finding data structure (matching the backend format)
export interface FindingInfo {
  finding_id: string;
  content: string;
  source_type?: string;
  source_id?: string;
  source_document?: string;
  source_reference?: string;
  confidence_score?: number;
}

interface NodeDetailPanelProps {
  node: NodeDetailData | null;
  findings?: Record<string, FindingInfo>;
  onClose: () => void;
  onFindingClick?: (findingId: string) => void;
  onOpenNode?: (nodeId: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export default function NodeDetailPanel({
  node,
  findings,
  onClose,
  onFindingClick,
  onOpenNode,
}: NodeDetailPanelProps) {
  const [panelHeight, setPanelHeight] = useState(250);
  const isResizing = useRef(false);
  const startY = useRef(0);
  const startHeight = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isResizing.current = true;
    startY.current = e.clientY;
    startHeight.current = panelHeight;
    e.preventDefault();
  }, [panelHeight]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      // Dragging up increases height (negative delta)
      const delta = startY.current - e.clientY;
      const newHeight = Math.min(Math.max(startHeight.current + delta, 100), window.innerHeight * 0.7);
      setPanelHeight(newHeight);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (!node) return null;

  // Helper to get finding label in format "CRR - Article 89"
  const getFindingLabel = (findingId: string, idx: number): string => {
    const finding = findings?.[findingId];
    const doc = finding?.source_document;
    const ref = finding?.source_reference;

    if (doc && ref) {
      return `${doc} - ${ref}`;
    }
    if (ref) {
      return ref;
    }
    if (doc) {
      return doc;
    }
    return `Evidence ${idx + 1}`;
  };

  const color = getNodeTypeColor(node.nodeType);
  // For plan nodes, show workspace type label (Research/Build/Context) instead of generic node type
  const WORKSPACE_TYPE_LABELS: Record<string, string> = {
    research: 'Research',
    production: 'Build',
    institution_context: 'Context',
  };
  const typeLabel = (node.workspaceType && WORKSPACE_TYPE_LABELS[node.workspaceType])
    ? WORKSPACE_TYPE_LABELS[node.workspaceType]
    : getNodeTypeLabel(node.nodeType);
  const hasFindingIds = node.findingIds && node.findingIds.length > 0;
  const hasContent = node.content || node.detail;
  const canOpenNode = Boolean(node.workspaceId && onOpenNode);

  return (
    <div
      className="border-t border-border-default bg-surface flex flex-col"
      style={{ height: panelHeight }}
    >
      {/* Resize Handle */}
      <div
        onMouseDown={handleMouseDown}
        className="h-2 cursor-ns-resize bg-bg-warm hover:bg-divider flex items-center justify-center flex-shrink-0 border-b border-border-default transition-colors"
        title="Drag to resize"
      >
        <GripHorizontal size={14} strokeWidth={1.5} className="text-fg-tertiary" />
      </div>
      {/* Header */}
      <div className="flex-shrink-0 px-4 py-2 flex items-center justify-between bg-bg-warm/50 border-b border-divider">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Node type badge — tinted in node-type color */}
          <div
            className="flex items-center gap-1.5 px-2 py-1 rounded-md font-body text-[10px] font-bold uppercase tracking-[0.08em] flex-shrink-0"
            style={{ backgroundColor: `${color}14`, color }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
            <span>{typeLabel}</span>
          </div>

          {/* Node label — full display */}
          <h3 className="text-[17px] font-semibold text-fg min-w-0 truncate">
            {node.label}
          </h3>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {canOpenNode && (
            <button
              type="button"
              onClick={() => onOpenNode?.(node.nodeId)}
              className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover active:translate-y-px"
              title="Open node"
            >
              <ExternalLink size={16} strokeWidth={1.5} />
              Open node
            </button>
          )}

          <button
            onClick={onClose}
            className="p-1.5 hover:bg-bg-warm rounded-md transition-colors text-fg-tertiary hover:text-fg"
            title="Close"
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Content - Show relevance note (reasoning) and/or content */}
      {(hasContent || node.relevanceNote) && (
        <div className="flex-1 overflow-y-auto flex min-h-0">
          {/* Main content area */}
          <div className="flex-1 p-4 overflow-y-auto space-y-4">
            {/* Relevance Note — the reasoning/logic, shown first with warning rail */}
            {node.relevanceNote && (
              <div className="p-3 bg-warning-bg rounded-md border-l-[3px] border-warning">
                <h4 className="font-body text-[10px] font-bold text-warning uppercase tracking-[0.08em] mb-1">
              Why this matters
                </h4>
                <p className="text-sm text-fg leading-relaxed">
                  {node.relevanceNote}
                </p>
              </div>
            )}

            {/* Plan nodes: structured Input / Task / Output view */}
            {node.workspaceType && (node.inputs?.length || node.deliverable) ? (
              <div className="space-y-3">
                {/* Inputs */}
                {node.inputs && node.inputs.length > 0 && (
                  <div>
                    <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-1.5">
                      Inputs
                    </h4>
                    <div className="space-y-1">
                      {node.inputs.map((inp, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-fg-secondary">
                          <span className="text-fg-tertiary">←</span>
                          <span className="font-medium">{inp.deliverable}</span>
                          <span className="text-xs text-fg-tertiary">from {inp.fromLabel}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Task */}
                {hasContent && (
                  <div>
                    <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-1.5">
                      Task
                    </h4>
                    <p className="text-sm text-fg leading-relaxed">
                      {node.content || node.detail}
                    </p>
                  </div>
                )}

                {/* Deliverable */}
                {node.deliverable && (
                  <div>
                    <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-1.5">
                      Deliverable
                    </h4>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-fg-tertiary">→</span>
                      <span className="font-semibold" style={{ color }}>{node.deliverable}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Regular nodes — plain content (matches main). */
              hasContent && (
                <div>
                  <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-2">
                    Content
                  </h4>
                  <p className="text-sm text-fg leading-relaxed whitespace-pre-wrap">
                    {node.content || node.detail}
                  </p>
                </div>
              )
            )}
          </div>

          {/* Linked evidence sidebar (within panel) */}
          {hasFindingIds && (
            <div className="w-56 border-l border-divider bg-bg-warm/50 overflow-y-auto p-3 flex-shrink-0">
              <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-2">
              Linked evidence
              </h4>
              <p className="text-[11px] text-fg-tertiary mb-3">
                Click to view full legal text
              </p>
              <div className="space-y-1.5">
                {node.findingIds!.map((findingId, idx) => (
                  <button
                    key={findingId}
                    onClick={() => onFindingClick?.(findingId)}
                    className="w-full text-left px-2 py-1.5 rounded-md border border-border-default bg-surface hover:border-accent hover:bg-accent-tint transition-colors group text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="flex-1 min-w-0 font-mono text-[11px] text-fg group-hover:text-accent-hover truncate">
                        {getFindingLabel(findingId, idx)}
                      </span>
                      <ExternalLink size={10} strokeWidth={1.5} className="text-fg-tertiary group-hover:text-accent flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Linked evidence only (no content or relevance note) */}
      {!hasContent && !node.relevanceNote && hasFindingIds && (
        <div className="p-3">
          <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-2">
              Linked evidence
          </h4>
          <p className="text-[11px] text-fg-tertiary mb-3">
            Click to view full legal text
          </p>
          <div className="flex gap-2 flex-wrap">
            {node.findingIds!.map((findingId, idx) => (
              <button
                key={findingId}
                onClick={() => onFindingClick?.(findingId)}
                className="px-3 py-1.5 rounded-md border border-border-default bg-surface hover:border-accent hover:bg-accent-tint transition-colors font-mono text-[11px] font-medium text-fg hover:text-accent-hover flex items-center gap-1.5"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                <span>{getFindingLabel(findingId, idx)}</span>
                <ExternalLink size={10} strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Validation status (#89) */}
      {node.validationStatus && (
        <div className="px-4 py-3 border-t border-divider">
          <h4 className="font-body text-[10px] font-bold text-fg-tertiary uppercase tracking-[0.08em] mb-2">
            Validation
          </h4>
          <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full font-mono text-[11px] font-semibold uppercase tracking-wider ${
            node.validationStatus === 'approved' ? 'text-success bg-success-bg' :
            node.validationStatus === 'rejected' ? 'text-danger bg-danger-bg' :
            node.validationStatus === 'review_needed' ? 'text-warning bg-warning-bg' :
            'text-fg-tertiary bg-bg-warm'
          }`}>
            {node.validationStatus === 'approved' ? '✓' :
             node.validationStatus === 'rejected' ? '✗' :
             node.validationStatus === 'review_needed' ? '●' : '○'}
            {' '}{node.validationStatus.replace('_', ' ')}
          </div>

          {/* Review rationale */}
          {node.reviews && node.reviews.length > 0 && (
            <div className="mt-2 space-y-2">
              {node.reviews.map((review, idx) => (
                <div key={idx} className={`text-xs p-2 rounded-md border ${
                  review.verdict === 'approved' ? 'border-success bg-success-bg' :
                  review.verdict === 'rejected' || review.verdict === 'flagged' ? 'border-danger bg-danger-bg' :
                  'border-border-default bg-bg-warm'
                }`}>
                  <div className="flex items-center gap-1 text-fg-tertiary mb-1">
                    <span className="font-medium">{review.reviewer_id}</span>
                    <span>·</span>
                    <span>{review.channel}</span>
                  </div>
                  {/* pre-wrap: the severity gate appends its own lines to a
                      rationale ("[severity-rubric: ...]", "[re-raised: dismissed
                      by author: ...]"). Those are the reader's only signal that a
                      finding was capped rather than resolved, so they must not be
                      reflowed into the reviewer's sentence. */}
                  <p className="text-fg whitespace-pre-wrap">{review.rationale}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!hasContent && !node.relevanceNote && !hasFindingIds && !node.validationStatus && (
        <div className="p-4 text-sm text-fg-tertiary italic">
          No additional details available.
        </div>
      )}
    </div>
  );
}
